<template>
  <Drawer
    v-model:open="isDrawerOpen"
    :vaul-drawer-wrapper="false"
    @update:open="(e) => onclose(e)"
  >
    <DrawerContent
      class="rounded-t-3xl !bg-[linear-gradient(0deg,#f7f9fb_77.19%,#e1f0ff_100%)]"
    >
      <div
        class="max-w-5xl mx-auto flex flex-col items-center w-full px-6 mb-2"
      >
        <div class="flex justify-center gap-x-2 items-center w-full mt-1">
          <h2 class="text-[#101828] text-lg font-bold">AI Preview</h2>
          <Icon
            height="24"
            icon="mingcute:ai-line"
            class="text-[#266EF1]"
            @click="handleClose"
          />
        </div>
        <div class="flex justify-center items-center gap-3 mt-6">
          <div
            v-for="(mockup, index) in mockups"
            :key="index"
            :class="[
              'rounded-xl overflow-hidden shadow-md pointer-events-none',
              getSizeClass(index),
            ]"
          >
            <Mockup
              class="rounded-md overflow-hidden"
              :data="{ design, color }"
              :config="mockup"
            />
          </div>
        </div>

        <div class="mt-6 w-full">
          <div v-if="!isColorPickerSelected" class="flex gap-x-3">
            <ImagePicker
              class="w-full h-11"
              btn-class="w-full px-4 lg:!px-1"
              :default-image="design"
              label="upload-design"
              storage-key="design-file"
              @upload="handleFileUpload"
            />
            <ColorSelector
              v-model:is-color-picker-selected="isColorPickerSelected"
              class="w-full h-11"
              @color-update="handleColorUpdate"
            />
          </div>
          <div v-else class="flex gap-x-3">
            <div
              class="py-2 px-4 bg-white rounded-md border"
              @click="isColorPickerSelected = false"
            >
              <Icon icon="solar:arrow-left-linear" height="16" />
            </div>
            <div class="py-2.5 px-3 bg-white rounded-md border grow">
              <ColorSlider v-model="swatchColor" class="h-3 max-h-3" />
            </div>
          </div>
        </div>
        <div class="my-6 w-full">
          <Button class="w-full" @click="handleClick">Check out preview</Button>
        </div>
      </div>
    </DrawerContent>
  </Drawer>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from "vue";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import Button from "@/components/ui/button/Button.vue";
import { openPreviewDrawer } from "@/lib/utils.js";
import { useSubscription } from "@vueuse/rxjs";
import { Icon } from "@iconify/vue";
import ImagePicker from "@/components/common/ImagePicker.vue";
import ColorSelector from "@/components/listing/ColorSelector.vue";
import { Slider as ColorSlider } from "@ckpack/vue-color";
import { throttle } from "es-toolkit";
import { useMockupStore } from "@/datastore/module/mockup.js";
import Mockup from "@/components/listing/Mockup.vue";
import placeHolderImage from "@/assets/designPlaceholder.png";
import { getImageUrlFromLocalStorage } from "@/util/common.util";

const store = useMockupStore();

const mockups = computed(() => {
  const firstTen = store.filteredData?.slice(0, 10);
  const freeMock = firstTen.filter((mockup) => mockup?.pro === false);
  return freeMock?.slice(-5);
});

const emits = defineEmits(["upload", "colorUpdate"]);

const isDrawerOpen = ref(false);
const design = ref(null);
const color = ref({ hex: "#FFFFFF" });
const swatchColor = ref("#1976D2FF");
const isColorPickerSelected = ref(false);

useSubscription(
  openPreviewDrawer.subscribe(() => {
    isDrawerOpen.value = true;
  }),
);

onMounted(async () => {
  let designUrl = await getImageUrlFromLocalStorage("design-file");
  if (designUrl) {
    design.value = designUrl;
  } else {
    design.value = placeHolderImage;
  }
});

const throttledColorHandler = throttle((newColor) => {
  color.value = newColor;
  emits("colorUpdate", newColor);
}, 100);

watch(
  () => swatchColor,
  (newVal) => {
    throttledColorHandler(newVal.value);
  },
  { deep: true },
);

const onclose = (state) => {
  if (!state) {
    isDrawerOpen.value = false;
    document.body.style.background = "";
  }
};

const handleClick = () => {
  isDrawerOpen.value = false;
};

const handleClose = () => {
  isDrawerOpen.value = false;
  document.body.style.background = "";
};

const handleFileUpload = async (file) => {
  design.value = file;
  emits("upload", file);
};

const handleColorUpdate = async (newColor) => {
  color.value = { hex: newColor };
  emits("colorUpdate", { hex: newColor });
};

function getSizeClass(index) {
  if (index === 2) return "w-32 h-40";
  if (index === 1 || index === 3) return "w-[100px] h-20";
  return "w-12 h-[60px]";
}
</script>
<style scoped>
.background-image {
  background-image: url("../../assets/previewDrawerBG.png");
}
</style>
